import { Injectable, inject } from '@angular/core';
import { AngularFireStorage, AngularFireUploadTask } from '@angular/fire/compat/storage';
import { getDownloadURL, getStorage, deleteObject, listAll, StorageReference } from '@angular/fire/storage';
import { ref } from 'firebase/storage';
import { lastValueFrom } from 'rxjs';

/**
 * Represents the metadata of an object stored in Firebase Storage.
 */
export interface ObjectStorageData {
  /** The download URL of the stored object. */
  url: string;
  /** The full path of the object in the storage bucket (e.g., 'folder/subfolder/file.txt'). */
  path: string;
  /** The name of the Firebase Storage bucket where the object is stored. */
  bucket: string;
  /** The name of the object file (e.g., 'file.txt'). */
  name?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MultiObjectStorageService {
  private storage = inject(AngularFireStorage);

  /**
   * Uploads a Blob or File object to a specified path in Firebase Storage.
   * @param objectToUpload The Blob or File to upload.
   * @param path The desired storage path (e.g., 'documents/report.pdf').
   * @returns A promise that resolves with the storage metadata of the uploaded object.
   * @throws Throws an error if the upload fails.
   */
  public async uploadObject(objectToUpload: Blob | File, path: string): Promise<ObjectStorageData> {
    try {
      const refStorage = this.storage.ref(path);
      const task = await refStorage.put(objectToUpload);
      const { fullPath, bucket, name } = task.metadata;
      const url = await lastValueFrom(refStorage.getDownloadURL());
      const storageData: ObjectStorageData = { url, path: fullPath, bucket, name };
      return storageData;
    } catch (error) {
      console.error(`Error uploading object to path "${path}": `, error);
      // Re-throw the error to allow calling code to handle it
      throw error;
    }
  }

  /**
   * Deletes all objects within a specified directory path in Firebase Storage.
   * WARNING: Use with extreme caution as this will permanently delete all files in the directory.
   * @param directoryPath The path to the directory to delete (e.g., 'users/userId/files/').
   * @returns A promise that resolves when the deletion attempt is complete.
   */
  public async deleteDirectory(directoryPath: string): Promise<void> {
    const storage = getStorage();
    const directoryRef = ref(storage, directoryPath);

    try {
      const res = await listAll(directoryRef);
      const deletePromises: Promise<void>[] = [];
      res.items.forEach((itemRef: StorageReference) => {
        console.log(`Deleting object: ${itemRef.fullPath}`);
        deletePromises.push(deleteObject(itemRef));
      });
      // You might want to handle potential errors during individual deletions if needed
      await Promise.all(deletePromises);
      console.log(`Successfully deleted objects in directory: ${directoryPath}`);
    } catch (error) {
      console.error(`Error deleting objects in directory "${directoryPath}":`, error);
      // Optionally re-throw or handle the error appropriately
      throw error;
    }
  }

  /**
   * Deletes a single object from Firebase Storage based on its full path.
   * @param objectPath The full path of the object to delete (e.g., 'images/profile.jpg').
   * @returns A promise that resolves when the deletion is complete.
   */
  public async deleteObjectByPath(objectPath: string): Promise<void> {
    const storageRef = this.storage.ref(objectPath);
    try {
      await lastValueFrom(storageRef.delete());
      console.log(`Object deleted successfully: ${objectPath}`);
    } catch (error) {
      console.error(`Error deleting object at path "${objectPath}":`, error);
      // Optionally re-throw or handle the error appropriately
      throw error;
    }
  }

  /**
   * Private helper to get metadata after an upload task completes.
   * @param task The AngularFireUploadTask.
   * @returns A promise resolving with the object's storage metadata.
   */
  private async uploadAndGetObjectMetadata(task: AngularFireUploadTask): Promise<ObjectStorageData> {
    const snap = await task;
    const { fullPath, bucket, name } = snap.metadata;
    const storage = getStorage();
    const storageRef = ref(storage, fullPath);
    const url = await getDownloadURL(storageRef);
    const meta: ObjectStorageData = {
      url,
      path: fullPath, // Use fullPath for the 'path' property
      bucket,
      name,
    };
    return meta;
  }
}
